import type { ClassValue } from "clsx";
import type * as React from "react";

import { bareFieldClass, FIELD_GEOMETRY, fieldClass } from "../../lib/surfaces";

/**
 * Where a field sits: `control`, the recessed field (the default); `bare`, no frame, fill or focus
 * halo, inside a frame that shows its focus (an input group, a search row); `document`, bare text
 * edited in place (a title), with a faint outline on keyboard focus.
 */
export type FieldAppearance = "control" | "bare" | "document";

/** A field's classes for an appearance, its geometry included. */
export function fieldAppearanceClass(appearance: FieldAppearance, ...classes: ClassValue[]): string {
	if (appearance === "control") return fieldClass(FIELD_GEOMETRY, ...classes);
	return bareFieldClass(
		FIELD_GEOMETRY,
		appearance === "document" &&
			"px-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-ring/30",
		...classes,
	);
}

/** The design system's recessed text field (`@crow/ui`), at Crowdex's density-aware height. */
function Input({
	className,
	type,
	appearance = "control",
	...props
}: React.ComponentProps<"input"> & { appearance?: FieldAppearance }) {
	return (
		<input
			type={type}
			data-slot="input"
			data-appearance={appearance}
			className={fieldAppearanceClass(
				appearance,
				"min-w-0 py-1 file:inline-flex file:h-control-xs file:border-0 file:bg-transparent file:text-ui file:font-medium file:text-foreground disabled:pointer-events-none",
				className,
			)}
			{...props}
		/>
	);
}

export { Input };
